import { useEffect } from "react";

// Pins a react-data-table-component header under the fixed top nav while its table
// scrolls with the page. The library's responsive wrapper sets overflow-x, which breaks
// position: sticky, so the header is shifted with a transform instead.
const getNavOffset = () => {
  const nav = document.querySelector(".nav-container");
  if (!nav || window.getComputedStyle(nav).position !== "fixed") {
    return 0;
  }
  return Math.max(nav.getBoundingClientRect().bottom, 0);
};

const useStickyTableHead = (containerRef) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    let frame = null;

    const update = () => {
      frame = null;
      const table = container.querySelector(".rdt_Table");
      const head = container.querySelector(".rdt_TableHead");
      if (!table || !head) {
        return;
      }

      const tableRect = table.getBoundingClientRect();
      const maxShift = Math.max(tableRect.height - head.offsetHeight, 0);
      const shift = Math.min(Math.max(getNavOffset() - tableRect.top, 0), maxShift);

      head.style.transform = shift ? `translateY(${shift}px)` : "";
      head.classList.toggle("is-stuck", shift > 0);
    };

    const scheduleUpdate = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    const listenerOptions = { capture: true, passive: true };
    document.addEventListener("scroll", scheduleUpdate, listenerOptions);
    window.addEventListener("resize", scheduleUpdate, listenerOptions);

    const mutationObserver = new MutationObserver(scheduleUpdate);
    mutationObserver.observe(container, { childList: true, subtree: true });

    const resizeObserver = window.ResizeObserver ? new ResizeObserver(scheduleUpdate) : null;
    resizeObserver?.observe(container.firstElementChild || container);

    scheduleUpdate();

    return () => {
      document.removeEventListener("scroll", scheduleUpdate, listenerOptions);
      window.removeEventListener("resize", scheduleUpdate, listenerOptions);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [containerRef]);
};

export default useStickyTableHead;
